import { ChangeEvent, useState } from 'react';
import { Button, Input } from 'components/atoms/index';
import { useLocation, useNavigate } from 'react-router-dom';
import { studentAuthApi } from '@/services/api';
import { Modal } from '@/components/organisms/Modal/Modal';
import { getSlugPrefix } from '@/utils/slug';

type FormField = 'name' | 'birthDate' | 'academyCode';

const createInitialFormState = (): Record<FormField, string> => ({
  name: '',
  birthDate: '',
  academyCode: '',
});

const createInitialTouchedState = (): Record<FormField, boolean> => ({
  name: false,
  birthDate: false,
  academyCode: false,
});

const createInitialErrorState = (): Record<FormField, string> => ({
  name: '',
  birthDate: '',
  academyCode: '',
});

export default function StudentFindId() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const slugPrefix = getSlugPrefix(pathname);
  const buildPath = (path: string) => (slugPrefix ? `${slugPrefix}${path}` : path);

  const [form, setForm] = useState<Record<FormField, string>>(() => createInitialFormState());
  const [touched, setTouched] = useState<Record<FormField, boolean>>(() => createInitialTouchedState());
  const [errors, setErrors] = useState<Record<FormField, string>>(() => createInitialErrorState());
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [foundId, setFoundId] = useState('');

  const maskLoginId = (loginId: string) => {
    if (!loginId) return '';
    if (loginId.includes('*')) {
      return loginId;
    }
    if (loginId.length <= 3) return '***';
    // [QA Fix] 마지막 3자리만 마스킹하여 스펙과 동일하게 노출
    return loginId.slice(0, -3) + '***';
  };

  const formatBirthDate = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) {
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    }
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  };

  const isValidBirthDate = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 8) return false;

    const year = Number(digits.slice(0, 4));
    const month = Number(digits.slice(4, 6));
    const day = Number(digits.slice(6));
    const date = new Date(`${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`);
    if (Number.isNaN(date.getTime())) return false;
    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
  };

  const validateField = (field: FormField, value: string) => {
    switch (field) {
      case 'name':
        if (!value.trim()) return '이름을 입력해주세요.';
        return '';
      case 'birthDate':
        if (!value.trim()) return '생년월일을 입력해주세요.';
        if (!isValidBirthDate(value)) return '유효한 생년월일을 입력해주세요.';
        return '';
      case 'academyCode':
        if (!value.trim()) return '학원코드를 입력해주세요.';
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field: FormField) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, form[field]) }));
  };

  const handleChange = (field: FormField) => (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const value =
      field === 'birthDate'
        ? formatBirthDate(rawValue)
        : field === 'academyCode'
          ? rawValue.toUpperCase()
          : rawValue;

    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
  };

  const validateForm = () => {
    const nextErrors: Record<FormField, string> = {
      name: validateField('name', form.name),
      birthDate: validateField('birthDate', form.birthDate),
      academyCode: validateField('academyCode', form.academyCode),
    };
    setErrors(nextErrors);
    const hasError = Object.values(nextErrors).some(Boolean);
    if (hasError) {
      setTouched({ name: true, birthDate: true, academyCode: true });
    }
    return !hasError;
  };

  const handleFindId = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await studentAuthApi.findId({
        name: form.name.trim(),
        birthDate: form.birthDate,
        academyCode: form.academyCode.trim(),
      });
      if (response?.success && response?.data?.loginId) {
        setFoundId(maskLoginId(response.data.loginId));
        setStep(2);
      } else {
        await Modal.alert({ message: response?.message || '아이디 정보를 찾을 수 없습니다.' });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || '아이디 찾기 중 오류가 발생했습니다.';
      await Modal.alert({ message });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setForm(createInitialFormState());
    setTouched(createInitialTouchedState());
    setErrors(createInitialErrorState());
    setStep(1);
    setFoundId('');
  };

  const goLogin = () => navigate(buildPath('/login'));

  return (
    <main className='flex flex-1 justify-center my-10 w-full bg-white card md:py-10 md:bg-transparent'>
        <div className='flex-1 px-5 pb-8 lg:px-10'>
            <h1 className='pb-5 heading1'>아이디 찾기</h1>

        {step === 1 && (
            <section>
                <div className='flex flex-col gap-5'>
              <div className='w-full'>
                    <Input
                  name='name'
                    label='이름'
                    placeholder='이름을 입력해주세요.'
                  value={form.name}
                  onChange={handleChange('name')}
                  onBlur={handleBlur('name')}
                  disabled={isLoading}
                    />
                {touched.name && errors.name && <small className='error_msg'>{errors.name}</small>}
              </div>
                    
              <div className='w-full'>
                    <Input
                  name='birthDate'
                    label='생년월일'
                  placeholder='예) 2003-01-10'
                  value={form.birthDate}
                  onChange={handleChange('birthDate')}
                  onBlur={handleBlur('birthDate')}
                  disabled={isLoading}
                  inputMode='numeric'
                  maxLength={10}
                />
                {touched.birthDate && errors.birthDate && (
                  <small className='error_msg'>{errors.birthDate}</small>
                )}
              </div>

              <div className='w-full'>
                    <Input      
                  name='academyCode'
                    label='학원코드'
                    placeholder='학원코드를 입력해주세요.'
                  value={form.academyCode}
                  onChange={handleChange('academyCode')}
                  onBlur={handleBlur('academyCode')}
                  disabled={isLoading}
                    />
                {touched.academyCode && errors.academyCode && (
                  <small className='error_msg'>{errors.academyCode}</small>
                )}
              </div>
                </div>

            <Button className='mt-10 w-full' onClick={handleFindId} disabled={isLoading}>
              {isLoading ? '조회 중...' : '아이디 찾기'}
            </Button>
            </section>
        )}

        {step === 2 && (
          <section>
            <div className='p-5 text-center bg-gray-50 rounded-lg'>
              <p className='text-lg font-bold'>
                회원님의 아이디는 {foundId || '확인되지 않았습니다.'}
              </p>
                </div>
            <div className='flex gap-5 mt-10'>
              <Button className='w-full' intent='secondary' onClick={resetForm} disabled={isLoading}>
                다시 조회하기
              </Button>
              <Button className='w-full' onClick={goLogin}>
                로그인 하기
              </Button>
                </div>
          </section>
        )}
        </div>
    </main>
  );
}